import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { MenuItem } from 'src/app/shared/model/Menu';

/**
 * Uma fatia de até `MenuListPage.CHUNK_SIZE` produtos já materializados de uma categoria.
 * `products` só troca de referência enquanto a fatia está sendo preenchida; depois disso
 * nunca mais muda, e é isso que tira a fatia do change detection.
 */
export interface CardChunk {
	index: number;
	products: MenuItem[];
}

/** Categoria da lista geral como a lista a exibe (ver `MenuListPage.advance`). */
export interface CategoryView {
	category: any;
	/** Produtos VISÍVEIS da categoria — a fonte de onde o pump fatia. */
	allProducts: MenuItem[];
	/** Quantos de `allProducts` já foram materializados (sempre um prefixo). */
	materialized: number;
	/** `allProducts[0..materialized)` agrupados em fatias; só a última pode estar incompleta. */
	chunks: CardChunk[];
	/** Sobe a cada mudança em `chunks`: é o input que faz um refresh do PAI alcançar o filho OnPush. */
	revision: number;
	hasProducts: boolean;
	hasDescription: boolean;
	descriptionHtml: any;
	descriptionText: string;
}

/**
 * Fronteira de change detection de UMA fatia de cards.
 *
 * Existe por um motivo só: o `ngForOf` re-diffa a coleção inteira (e re-checa a view
 * embutida de cada item) toda vez que a view que o contém é checada. Com os cards de
 * todas as categorias no template do `MenuListPage`, cada fatia nova do render
 * progressivo custava um diff sobre TUDO o que já estava montado — o custo do pump
 * crescia com o quadrado do cardápio. Numa fatia OnPush cujo `products` já não muda,
 * o change detection nem entra.
 *
 * Seletor de ATRIBUTO num `<div>` puro: é o único nó que a fatia acrescenta (1 a cada
 * 48 produtos). Um bloco sem padding nem borda deixa a margem dos cards colapsar como
 * antes, então o layout não muda (medido: a lista da 8466 mede os mesmos 225.049 px).
 */
@Component({
	selector: '[mpCardChunk]',
	template: `<card
		*ngFor="let prod of products; trackBy: trackById"
		style="width: 100%"
		[item]="prod"
		[newHomePedirDelivery]="newHomePedirDelivery"
	></card>`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuCardChunkComponent {
	@Input() products: MenuItem[] = [];
	@Input() newHomePedirDelivery: boolean;

	// trackBy (Caso 16): trocar a referência do menu (busca no layout legado) reaproveita o DOM.
	trackById(index: number, item: any): any {
		return item?.id ?? index;
	}
}

/**
 * Fronteira de change detection de UMA categoria da lista geral.
 *
 * Hospeda-se no próprio `<div class="scroll-category">` (seletor de atributo) e PROJETA
 * o cabeçalho: o `<h1>`/descrição continuam declarados no template do `MenuListPage`,
 * então mantêm o `_ngcontent` dele e as regras de `menu-list.page.scss` seguem valendo
 * sem mudar especificidade nenhuma. O que vive aqui é só a lista de fatias.
 *
 * O pump chama `refresh()` na categoria que está sendo preenchida: o custo do tick vira
 * "fatias desta categoria + a fatia parcial", sem passar pelas outras 33 categorias. O
 * input `revision` garante que um refresh comum do pai (ex.: `renderAllNow()` +
 * `markForCheck()`) também chegue aqui.
 */
@Component({
	selector: '[mpCategoryCards]',
	template: `<ng-content></ng-content><div
		*ngFor="let chunk of view.chunks; trackBy: trackByChunk"
		mpCardChunk
		[products]="chunk.products"
		[newHomePedirDelivery]="newHomePedirDelivery"
	></div>`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuCategoryCardsComponent {
	@Input() view: CategoryView;
	@Input() revision: number;
	@Input() newHomePedirDelivery: boolean;

	constructor(private cdr: ChangeDetectorRef) {}

	trackByChunk(index: number, chunk: CardChunk): number {
		return chunk.index;
	}

	/** Change detection SÓ desta categoria (e das fatias cujo `products` mudou). */
	refresh() {
		this.cdr.detectChanges();
	}
}
